import { Unit, Lease } from '@urbanmix-tech/shared-js';

export interface UnitPricingInput {
  unitId: string;
  rentIncreasePercent: number;
  concessionMonths: number;
  leaseLengthMonths: number;
  isPriced: boolean;
  draftLeaseId?: string;
}

export interface PricingWizardFilters {
  expiresInDays: number;
  showAffordable: boolean;
  showMarket: boolean;
  selectedCategoryKeys: string[];
}

export interface PricingWizardState {
  // Step tracking
  activeStep: number;

  // Step 1: Unit Selection
  selectedUnitIds: Set<string>;
  filters: PricingWizardFilters;

  // Step 2: Pricing
  pricingMode: 'single' | 'batch';
  currentSingleUnitIndex: number;
  unitPricingData: Map<string, UnitPricingInput>;

  // Step 3 & 4
  draftLeaseIds: Set<string>;
  selectedForApproval: Set<string>;
}

export interface PricingWizardActions {
  // Navigation
  setActiveStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;

  // Step 1: Unit Selection
  toggleUnitSelection: (unitId: string) => void;
  selectAllUnits: (unitIds: string[]) => void;
  clearUnitSelection: () => void;
  setFilters: (filters: Partial<PricingWizardFilters>) => void;

  // Step 2: Pricing
  setPricingMode: (mode: 'single' | 'batch') => void;
  setCurrentSingleUnitIndex: (index: number) => void;
  updateUnitPricing: (unitId: string, pricing: Partial<UnitPricingInput>) => void;
  markUnitAsPriced: (unitId: string, draftLeaseId: string) => void;

  // Step 3 & 4
  addDraftLease: (leaseId: string) => void;
  toggleApprovalSelection: (leaseId: string) => void;
  selectAllForApproval: () => void;
  clearApprovalSelection: () => void;

  // Reset
  resetWizard: () => void;
}

export interface PricingWizardContextValue {
  state: PricingWizardState;
  actions: PricingWizardActions;

  // Derived data
  selectedUnits: Unit[];
  pricedUnitsCount: number;
  totalSelectedCount: number;
  currentUnit: Unit | null;
}

// Props for step components
export interface StepProps {
  units: Unit[];
  leases: Lease[];
  onNext: () => void;
  onBack: () => void;
}

export const WIZARD_STEPS = [
  'Select Units',
  'Price Units',
  'Review',
  'Approve',
] as const;

export type WizardStepName = typeof WIZARD_STEPS[number];

export const WIZARD_STORAGE_KEY = 'pricingWizardState';
export const WIZARD_FILTERS_STORAGE_KEY = 'pricingWizardFilters';

export const DEFAULT_FILTERS: PricingWizardFilters = {
  expiresInDays: 500,
  showAffordable: true,
  showMarket: true,
  selectedCategoryKeys: ['0', '20', '30', '40', '60', '90', '120', 'rest'],
};

export const DEFAULT_PRICING_INPUT: Omit<UnitPricingInput, 'unitId'> = {
  rentIncreasePercent: 0,
  concessionMonths: 0,
  leaseLengthMonths: 12,
  isPriced: false,
};
